import * as c from "@yahshi/contracts";
import { z } from "zod";
import { createAdminRequest, type AdminHttpOptions } from "../../lib/adminHttp";
export type AssetPage<T> = { items: T[]; total: number };
export interface MembershipApi {
  eventHealth(q?: c.VirtualEventHealthQuery): Promise<c.VirtualEventHealth>;
  eventList(q?: c.VirtualEventQuery): Promise<AssetPage<c.VirtualEvent>>;
  eventDetail(id: string): Promise<c.VirtualEventDetail>;
  eventRetry(id: string, input: c.VirtualEventRetry): Promise<c.VirtualEvent>;
  statementList(q?: c.VirtualStatementQuery): Promise<AssetPage<c.VirtualStatementJob>>;
  statementDetail(id: string): Promise<c.VirtualStatementDetail>;
  statementCreate(input: c.VirtualStatementInput): Promise<c.VirtualStatementJob>;
  statementAction(id: string, input: c.VirtualStatementAction): Promise<c.VirtualStatementJob>;
  statementLink(id: string, key: string): Promise<z.infer<typeof c.virtualStatementLinkSchema>>;
  virtualWorkerStatus(q?: c.VirtualWorkerQuery): Promise<c.VirtualWorkerDashboard>;
  reconciliationList(q?: c.VirtualReconciliationQuery): Promise<AssetPage<c.VirtualReconciliationBatch>>;
  reconciliationCreate(input: c.VirtualReconciliationCreate): Promise<c.VirtualReconciliationBatch>;
  reconciliationDetail(id: string, q?: { page?: number; pageSize?: number }): Promise<c.VirtualReconciliationDetail>;
  reconciliationItems(id: string, q?: { page?: number; pageSize?: number }): Promise<c.VirtualReconciliationItems>;
  reconciliationResolve(batchId: string, id: string, input: c.VirtualDifferenceUpdate): Promise<c.VirtualReconciliationDifference>;
  virtualConfig(): Promise<{
    configs: c.VirtualConfig[];
    mappings: c.VirtualMapping[];
  }>;
  saveVirtualConfig(
    input: z.input<typeof c.virtualConfigInputSchema>,
  ): Promise<c.VirtualConfig>;
  saveVirtualMapping(
    input: z.input<typeof c.virtualMappingInputSchema>,
  ): Promise<c.VirtualMapping>;
  syncVirtual(id: string): Promise<z.infer<typeof c.virtualSyncResultSchema>>;
  refundVirtual(
    id: string,
    input: z.input<typeof c.virtualRefundInputSchema>,
  ): Promise<z.infer<typeof c.virtualRefundSchema>>;
  virtualSubscribe(
    input: z.input<typeof c.virtualSubscribeActionSchema>,
  ): Promise<z.infer<typeof c.virtualSubscribeResultSchema>>;
  pools(q?: c.AssetQuery): Promise<AssetPage<c.AssetPool>>;
  bindings(q?: c.AssetQuery): Promise<AssetPage<c.AssetBinding>>;
  products(q?: c.AssetQuery): Promise<AssetPage<c.AssetProduct>>;
  accounts(q?: c.AssetQuery): Promise<AssetPage<c.AssetAccount>>;
  purchaseTrace(id: string, q?: c.PurchaseTraceQuery): Promise<c.PurchaseTrace>;
  purchases(q?: c.PurchaseQuery): Promise<AssetPage<c.AssetPurchase>>;
  ledger(q?: c.AssetQuery): Promise<AssetPage<c.AssetLedger>>;
  audit(
    q?: c.AssetQuery,
  ): Promise<AssetPage<z.infer<typeof c.assetAuditSchema>>>;
  savePool(id: string | null, input: c.AssetPoolInput): Promise<c.AssetPool>;
  saveProduct(
    id: string | null,
    input: c.AssetProductInput,
  ): Promise<c.AssetProduct>;
  saveBinding(input: c.AssetBindingInput): Promise<c.AssetBinding>;
  adjust(input: c.AssetAdjustment): Promise<c.AssetAccount>;
  refundExchange(
    id: string,
    input: { mutationId: string; reason: string },
  ): Promise<c.AssetPurchase>;
}
export function createHttpMembershipApi(
  options: AdminHttpOptions,
): MembershipApi {
  const request = createAdminRequest(options),
    base = "/admin/membership";
  const list = <T>(kind: string, schema: z.ZodType<T>, q: c.AssetQuery = {}) =>
    request(
      `${base}/${kind}?${new URLSearchParams(Object.entries(c.membershipQuerySchema.parse(q)).map(([k, v]) => [k, String(v)]))}`,
      c.assetPageSchema(schema),
    );
  const write = <T>(
    path: string,
    method: string,
    input: unknown,
    schema: z.ZodType<T>,
  ) => request(base + path, schema, { method, body: JSON.stringify(input) });
  const queryString = (value: Record<string, unknown>) => new URLSearchParams(Object.entries(value).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)]));
  return {
    eventHealth: (q = {}) => request(`${base}/event-health?${queryString(c.virtualEventHealthQuerySchema.parse(q))}`, c.virtualEventHealthSchema),
    eventList: (q = {}) => request(`${base}/events?${queryString(c.virtualEventQuerySchema.parse(q))}`, c.assetPageSchema(c.virtualEventSchema)),
    eventDetail: id => request(`${base}/events/${z.uuid().parse(id)}`, c.virtualEventDetailSchema),
    eventRetry: (id, input) => write(`/events/${z.uuid().parse(id)}/retry`, "POST", c.virtualEventRetrySchema.parse(input), c.virtualEventSchema),
    statementList: (q = {}) => request(`${base}/statements?${queryString(c.virtualStatementQuerySchema.parse(q))}`, c.assetPageSchema(c.virtualStatementJobSchema)),
    statementDetail: id => request(`${base}/statements/${z.uuid().parse(id)}`, c.virtualStatementDetailSchema),
    statementCreate: input => write("/statements", "POST", c.virtualStatementInputSchema.parse(input), c.virtualStatementJobSchema),
    statementAction: (id, input) => write(`/statements/${z.uuid().parse(id)}/actions`, "POST", c.virtualStatementActionSchema.parse(input), c.virtualStatementJobSchema),
    statementLink: (id, key) => request(`${base}/statements/${z.uuid().parse(id)}/files/${encodeURIComponent(z.string().regex(/^[a-zA-Z0-9_-]{1,64}$/).parse(key))}`, c.virtualStatementLinkSchema),
    virtualWorkerStatus: (q = {}) => request(`${base}/worker-status?${queryString(c.virtualWorkerQuerySchema.parse(q))}`, c.virtualWorkerDashboardSchema),
    reconciliationList: (q = {}) => request(`${base}/reconciliation?${queryString(c.virtualReconciliationQuerySchema.parse(q))}`, c.assetPageSchema(c.virtualReconciliationBatchSchema)),
    reconciliationCreate: input => write("/reconciliation", "POST", c.virtualReconciliationCreateSchema.parse(input), c.virtualReconciliationBatchSchema),
    reconciliationDetail: (id, q = {}) => request(`${base}/reconciliation/${z.uuid().parse(id)}?${queryString(c.virtualReconciliationDetailQuerySchema.parse(q))}`, c.virtualReconciliationDetailSchema),
    reconciliationItems: (id, q = {}) => request(`${base}/reconciliation/${z.uuid().parse(id)}/items?${queryString(c.virtualReconciliationDetailQuerySchema.parse(q))}`, c.virtualReconciliationItemsSchema),
    reconciliationResolve: (batchId, id, input) => write(`/reconciliation/${z.uuid().parse(batchId)}/differences/${z.uuid().parse(id)}`, "PATCH", c.virtualDifferenceUpdateSchema.parse(input), c.virtualReconciliationDifferenceSchema),
    virtualConfig: () =>
      request(
        base + "/virtual",
        z.object({
          configs: z.array(c.virtualConfigSchema),
          mappings: z.array(c.virtualMappingSchema),
        }),
      ),
    saveVirtualConfig: (input) =>
      write(
        "/virtual/config",
        "PUT",
        c.virtualConfigInputSchema.parse(input),
        c.virtualConfigSchema,
      ),
    saveVirtualMapping: (input) =>
      write(
        "/virtual/mapping",
        "PUT",
        c.virtualMappingInputSchema.parse(input),
        c.virtualMappingSchema,
      ),
    virtualSubscribe: (input) =>
      write(
        "/virtual/subscribe",
        "POST",
        c.virtualSubscribeActionSchema.parse(input),
        c.virtualSubscribeResultSchema,
      ),
    syncVirtual: (id) =>
      write(
        `/virtual/${z.uuid().parse(id)}/sync`,
        "POST",
        {},
        c.virtualSyncResultSchema,
      ),
    refundVirtual: (id, input) =>
      write(
        `/virtual/${z.uuid().parse(id)}/refund`,
        "POST",
        c.virtualRefundInputSchema.parse(input),
        c.virtualRefundSchema,
      ),
    pools: (q) => list("pools", c.assetPoolSchema, q),
    bindings: (q) => list("bindings", c.assetBindingSchema, q),
    products: (q) => list("products", c.assetProductSchema, q),
    accounts: (q) => list("accounts", c.assetAccountSchema, q),
    purchases: (q = {}) => request(`${base}/purchases?${new URLSearchParams(Object.entries(c.purchaseQuerySchema.parse(q)).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)]))}`, c.assetPageSchema(c.assetPurchaseSchema)),
    purchaseTrace: (id, q = {}) => request(`${base}/purchases/${z.uuid().parse(id)}?${new URLSearchParams(Object.entries(c.purchaseTraceQuerySchema.parse(q)).map(([k, v]) => [k, String(v)]))}`, c.purchaseTraceSchema),
    ledger: (q) => list("ledger", c.assetLedgerSchema, q),
    audit: (q) => list("audit", c.assetAuditSchema, q),
    savePool: (id, input) =>
      write(
        `/pools${id ? `/${z.uuid().parse(id)}` : ""}`,
        id ? "PUT" : "POST",
        c.assetPoolInputSchema.parse(input),
        c.assetPoolSchema,
      ),
    saveProduct: (id, input) =>
      write(
        `/products${id ? `/${z.uuid().parse(id)}` : ""}`,
        id ? "PUT" : "POST",
        c.assetProductInputSchema.parse(input),
        c.assetProductSchema,
      ),
    saveBinding: (input) =>
      write(
        "/bindings",
        "PUT",
        c.assetBindingInputSchema.parse(input),
        c.assetBindingSchema,
      ),
    adjust: (input) =>
      write(
        "/adjustments",
        "POST",
        c.assetAdjustmentSchema.parse(input),
        c.assetAccountSchema,
      ),
    refundExchange: (id, input) =>
      write(
        `/purchases/${z.uuid().parse(id)}/refund-exchange`,
        "POST",
        input,
        c.assetPurchaseSchema,
      ),
  };
}
